import { defineStore, storeToRefs } from "pinia";
import { computed, ref, ComputedRef } from "vue";
import { Recipients } from "@/models/recipient";
import { MessageCompose, MsisdnOptionType } from "@/models/message-compose";
import { msisdnServices } from "@/services/msisdn.service";
import { useUserAccountsStore } from "./useUserAccountsStore";
import { addressTypes } from "@/helpers/address-types";
import { isNumeric, dynamicSort } from "@/helpers/utilities";
import { isNull, isUndefined, map, filter } from "underscore";
import { TagsDetails } from "@/models/tags";

export interface MessageComposeType {
  source?: string;
  sourceType?: string;
  isTTSMode?: boolean;
  isPriorityMessage?: boolean;
  recipients?: Recipients;
  deliveryTime?: Date;
  templateId?: string;
  message?: string;
  senderUsername?: string;
  tagIds?: TagsDetails[];
  contentType?: string;
  fromAddress?: string;
}

type MsisdnResponseType = {
  msisdn: string;
  msisdnId: string;
  name: string;
  type: string;
};

export interface MessageComposeState {
  recipients: Recipients;
  composeForm: MessageComposeType;
  msisdnOptions: MsisdnOptionType[];
  isCampaignSelected: boolean;
}

export const useMessageComposeStore = defineStore("messageCompose", () => {
  // STATES
  const recipients = ref<MessageComposeState["recipients"]>({});
  const composeForm = ref<MessageComposeState["composeForm"]>(
    new MessageCompose(),
  );
  const msisdnOptions = ref<MessageComposeState["msisdnOptions"]>([]);
  const isCampaignSelected = ref(false);

  // ACTIONS
  const setRecipients = (value: MessageComposeState["recipients"]) => {
    recipients.value = value;
  };

  const setForm = (form: MessageComposeType) => {
    composeForm.value = form;
  };

  const setMsisdnOptions = async () => {
    await msisdnServices.fetchAllMsisdn().then((response: any) => {
      if (response.data.length > 0) {
        const { getUserProfile } = storeToRefs(useUserAccountsStore());
        msisdnOptions.value = map(
          response.data,
          (msisdn: MsisdnResponseType) => {
            let showDefault = false;
            if (
              (isNull(getUserProfile.value?.defaultMsisdn) &&
                isUndefined(msisdn.msisdnId)) ||
              (!isNull(getUserProfile.value?.defaultMsisdn) &&
                getUserProfile.value?.defaultMsisdn === msisdn?.msisdnId)
            ) {
              showDefault = true;
            }
            return {
              id: msisdn?.msisdnId ?? null,
              label: `${msisdn.name} (${msisdn.msisdn})`,
              value: msisdn.msisdn,
              type: addressTypes[msisdn.type.toUpperCase()],
              originalType: msisdn.type,
              showDefault: showDefault,
            };
          },
        );
      }

      const msisdnNumericValues = filter(
        msisdnOptions.value,
        (msisdn: MsisdnOptionType) => msisdn.value && isNumeric(msisdn.value),
      );
      msisdnNumericValues.sort(dynamicSort("value"));

      const alphaStringValues = filter(
        msisdnOptions.value,
        (option: MsisdnOptionType) => !isNumeric(option.value),
      );
      alphaStringValues.sort(dynamicSort("value"));

      msisdnOptions.value = [...alphaStringValues, ...msisdnNumericValues];
    });
  };

  const setDisableSourceForCampaign = (value: boolean) => {
    isCampaignSelected.value = value;
  };

  const resetComposeForm = () => {
    composeForm.value = new MessageCompose();
  };

  // GETTERS
  const getRecipients: ComputedRef<MessageComposeState["recipients"]> =
    computed(() => recipients.value);

  const getForm: ComputedRef<MessageComposeState["composeForm"]> = computed(
    () => composeForm.value,
  );
  const getMsisdnOptions: ComputedRef<MessageComposeState["msisdnOptions"]> =
    computed(() => msisdnOptions.value);
  const getDisableSourceForCampaign: ComputedRef<boolean> = computed(
    () => isCampaignSelected.value,
  );

  return {
    getRecipients,
    getForm,
    getMsisdnOptions,
    getDisableSourceForCampaign,
    setDisableSourceForCampaign,
    setRecipients,
    setForm,
    setMsisdnOptions,
    resetComposeForm,
  };
});
